'use client';

import { faChevronDown, faSearch } from '@fortawesome/pro-light-svg-icons';
import { faBarsFilter } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@pickleballinc/ui/components/ui/button';
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerHeader,
	DrawerPortal,
	DrawerTitle
} from '@pickleballinc/ui/components/ui/drawer';
import { Icon } from '@pickleballinc/ui/components/ui/icon';
import { Input } from '@pickleballinc/ui/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@pickleballinc/ui/components/ui/select';
import clsx from 'clsx';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { useSeasonFromSearchParams } from '@/hooks/filters/useSeasonFromSearchParams';
import { Options } from '@/utils/types';

export interface ITeamsPageFilters {
	search: string;
	season: string;
	division: string;
	district: string;
	waiting_list: string;
	current_page: string;
	mobile_filters: string;
}

interface FiltersForTeamsPageProps {
	seasons: Options[];
	divisions: Options[];
	districts: Options[];
	activeSeason?: string;
}

const getNewSearchParams = (filters: ITeamsPageFilters) => {
	const newSearchParams = new URLSearchParams(window.location.search);

	if (filters.season) {
		newSearchParams.set('season', filters.season);
	} else {
		newSearchParams.delete('season');
	}

	if (filters.waiting_list && filters.waiting_list !== 'all') {
		newSearchParams.set('waiting_list', filters.waiting_list);
	} else {
		newSearchParams.delete('waiting_list');
	}

	if (filters.search) {
		newSearchParams.set('search', filters.search);
	} else {
		newSearchParams.delete('search');
	}
	if (filters.current_page) {
		newSearchParams.set('current_page', filters.current_page);
	} else {
		newSearchParams.delete('current_page');
	}

	if (filters.district && filters.district !== 'all') {
		newSearchParams.set('district', filters.district);
	} else {
		newSearchParams.delete('district');
	}

	if (filters.division && filters.division !== 'all') {
		newSearchParams.set('division', filters.division);
	} else {
		newSearchParams.delete('division');
	}

	newSearchParams.delete('preview_limit');

	return newSearchParams || '';
};

const waitingListOptions: Options[] = [
	{ value: 'all', label: 'All Teams' },
	{ value: 'true', label: 'Waiting list only' }
];

export const FiltersForTeamsPage = ({ seasons, divisions, districts, activeSeason }: FiltersForTeamsPageProps) => {
	const router = useRouter();
	const searchParams = useSearchParams();

	const [filters, setFilters] = useState<ITeamsPageFilters>({
		search: searchParams?.get('search') ?? '',
		season: searchParams?.get('season') ?? activeSeason ?? seasons[0]?.value ?? '',
		division: searchParams?.get('division') ?? '',
		district: searchParams?.get('district') ?? '',
		waiting_list: searchParams?.get('waiting_list') ?? '',
		current_page: searchParams?.get('current_page') ?? '',
		mobile_filters: ''
	});

	const divisionsWithAllOption: Options[] = divisions.length >= 1 ? [{ value: 'all', label: 'All divisions' }, ...divisions] : [];
	const districtsWithAllOption: Options[] = districts.length >= 1 ? [{ value: 'all', label: 'All districts' }, ...districts] : [];

	const emptyFilters: ITeamsPageFilters = {
		search: '',
		season: activeSeason ?? seasons[0]?.value ?? '',
		division: '',
		district: '',
		waiting_list: '',
		current_page: '',
		mobile_filters: ''
	};

	useEffect(() => {
		if (!searchParams.get('season')) {
			const params = getNewSearchParams(filters);
			router.replace(`?${params.toString()}`, { scroll: false });
		}
	}, []);

	const handleReset = () => {
		setFilters(emptyFilters);
		router.replace(`?${getNewSearchParams(emptyFilters).toString()}`);
	};

	useSeasonFromSearchParams(searchParams, setFilters, activeSeason ?? seasons[0]?.value);

	useEffect(() => {
		setFilters((prev) => ({
			...prev,
			division: searchParams?.get('division') ?? '',
			district: searchParams?.get('district') ?? ''
		}));
	}, [searchParams, setFilters]);

	return (
		<>
			<div className="flex flex-col gap-2 md:hidden mt-4">
				<div className="flex gap-2">
					<div className="relative w-full">
						<Input
							type="text"
							placeholder="Search team"
							value={filters.search}
							onChange={(e) => setFilters({ ...filters, search: e.target.value })}
							onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
								if (event.key === 'Enter') {
									router.replace(`?${getNewSearchParams(filters)?.toString()}`);
									event.currentTarget.blur();
								}
							}}
							className="h-10 w-full border border-gray-300 pl-8 bg-white focus-visible:ring-0"
							maxLength={100}
							data-testid="search-field"
						/>
						<FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
					</div>
					<Button
						className="flex w-fit md:hidden"
						size="lg"
						onClick={() => {
							router.replace(`?${getNewSearchParams(filters)?.toString()}`);
						}}
					>
						Search
					</Button>
				</div>
				<Button
					variant="outline"
					className="animate-fade-in-down flex w-full items-center justify-center mt-2 md:hidden"
					size="lg"
					onClick={() => setFilters({ ...filters, mobile_filters: 'true' })}
				>
					<FontAwesomeIcon icon={faBarsFilter} className="mr-2" />
					Filters
				</Button>
			</div>
			<div className="hidden md:grid md:grid-cols-2 gap-3 md:gap-x-6 md:gap-y-4 mt-4 lg:mt-6 lg:mb-2">
				<div>
					<Select onValueChange={(value) => setFilters({ ...filters, season: value })} value={filters.season || seasons[0]?.value}>
						<SelectTrigger
							className="bg-white focus:ring-0 h-10"
							icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
						>
							<SelectValue placeholder="Select season" />
						</SelectTrigger>
						<SelectContent>
							{seasons.map((season) => (
								<SelectItem key={season.value} value={season.value}>
									{season.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				<div>
					<Select
						onValueChange={(value) => setFilters({ ...filters, waiting_list: value })}
						value={filters.waiting_list || waitingListOptions[0]?.value}
					>
						<SelectTrigger
							id="waiting-lists-select"
							className="bg-white focus:ring-0 h-10"
							icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
						>
							<SelectValue placeholder="Select season" />
						</SelectTrigger>
						<SelectContent>
							{waitingListOptions.map((waitingListOption) => (
								<SelectItem key={waitingListOption.value} value={waitingListOption.value}>
									{waitingListOption.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				<div className="relative w-full">
					<Input
						type="text"
						placeholder="Search team"
						value={filters.search}
						onChange={(e) => setFilters({ ...filters, search: e.target.value })}
						onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
							if (event.key === 'Enter') {
								router.replace(`?${getNewSearchParams(filters)?.toString()}`);
								event.currentTarget.blur();
							}
						}}
						className="h-10 w-full border border-gray-300 pl-8 bg-white focus-visible:ring-0"
						maxLength={100}
						data-testid="search-field"
					/>
					<FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
				</div>
				<div className="hidden md:block" />

				{districts.length !== 1 && (
					<div className="flex items-start xl:items-center">
						<Select
							onValueChange={(value) => setFilters({ ...filters, district: value })}
							value={filters.district || districtsWithAllOption[0]?.value}
						>
							<SelectTrigger
								id="district-select"
								className="bg-white focus:ring-0 h-10"
								icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
							>
								<SelectValue placeholder="Select district" />
							</SelectTrigger>
							<SelectContent>
								{districtsWithAllOption.map((district) => (
									<SelectItem key={district.value} value={district.value}>
										{district.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				)}

				<div
					className={`flex flex-col xl:flex-row gap-3 items-start md:items-end xl:items-center w-full justify-between ${districts.length === 1 && 'col-span-2'}`}
				>
					<div className={`self-start ${districts.length === 1 ? 'w-full md:w-[49%]' : 'w-full'}`}>
						<Select
							onValueChange={(value) => setFilters({ ...filters, division: value })}
							value={filters.division || divisionsWithAllOption[0]?.value}
						>
							<SelectTrigger
								id="division-select"
								className={clsx('bg-white focus:ring-0 h-10', {
									hidden: divisions.length === 1
								})}
								icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
							>
								<SelectValue placeholder="Select division" />
							</SelectTrigger>
							<SelectContent>
								{divisionsWithAllOption.map((division) => (
									<SelectItem key={division.value} value={division.value}>
										{division.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div className="flex flex-row gap-4">
						<Button onClick={() => router.replace(`?${getNewSearchParams(filters)?.toString()}`)} size="lg">
							Apply
						</Button>

						<Button onClick={handleReset} size="lg" variant="outline">
							Reset
						</Button>
					</div>
				</div>
			</div>
			<MobileFiltersForTeamsPage
				filters={filters}
				setFilters={setFilters}
				handleReset={handleReset}
				seasons={seasons}
				districtsWithAllOption={districtsWithAllOption}
				divisionsWithAllOption={divisionsWithAllOption}
			/>
		</>
	);
};

interface MobileFiltersForTeamsPageProps {
	filters: ITeamsPageFilters;
	handleReset: () => void;
	setFilters: (filters: ITeamsPageFilters) => void;
	seasons: Options[];
	districtsWithAllOption: Options[];
	divisionsWithAllOption: Options[];
}

export const MobileFiltersForTeamsPage = ({
	filters,
	handleReset,
	setFilters,
	seasons,
	districtsWithAllOption,
	divisionsWithAllOption
}: MobileFiltersForTeamsPageProps) => {
	const router = useRouter();

	const isSliderOpen = filters.mobile_filters === 'true';

	return (
		<Drawer open={isSliderOpen} onClose={() => setFilters({ ...filters, mobile_filters: '' })}>
			<DrawerPortal>
				<DrawerContent className="flex w-full flex-col bg-white px-4 pb-4 pt-1 md:hidden h-[90%]">
					<DrawerDescription className="sr-only">Filters for teams page</DrawerDescription>
					<div className="overflow-y-auto">
						<DrawerHeader className="flex justify-between">
							<DrawerTitle>Filters</DrawerTitle>
							<DrawerClose>
								<Icon glyph="close" />
							</DrawerClose>
						</DrawerHeader>

						<div className="mt-6 w-full">
							<Select onValueChange={(value) => setFilters({ ...filters, season: value })} value={filters.season || seasons[0]?.value}>
								<SelectTrigger
									className="bg-white focus:ring-0 h-10"
									icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
								>
									<SelectValue placeholder="Select season" />
								</SelectTrigger>
								<SelectContent>
									{seasons.map((season) => (
										<SelectItem key={season.value} value={season.value}>
											{season.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className="mt-6 w-full">
							<Select
								onValueChange={(value) => setFilters({ ...filters, waiting_list: value })}
								value={filters.waiting_list || waitingListOptions[0]?.value}
							>
								<SelectTrigger
									id="waiting-lists-select"
									className="bg-white focus:ring-0 h-10"
									icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
								>
									<SelectValue placeholder="Select season" />
								</SelectTrigger>
								<SelectContent>
									{waitingListOptions.map((waitingListOption) => (
										<SelectItem key={waitingListOption.value} value={waitingListOption.value}>
											{waitingListOption.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						{districtsWithAllOption.length !== 1 && (
							<div className="mt-6 w-full">
								<Select
									onValueChange={(value) => setFilters({ ...filters, district: value })}
									value={filters.district || districtsWithAllOption[0]?.value}
								>
									<SelectTrigger
										id="district-select"
										className="bg-white focus:ring-0 h-10"
										icon={({ className }: { className: string }) => (
											<FontAwesomeIcon icon={faChevronDown} className={className} />
										)}
									>
										<SelectValue placeholder="Select district" />
									</SelectTrigger>
									<SelectContent>
										{districtsWithAllOption.map((district) => (
											<SelectItem key={district.value} value={district.value}>
												{district.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						)}
						<div className="mt-6">
							<Select
								onValueChange={(value) => setFilters({ ...filters, division: value })}
								value={filters.division || divisionsWithAllOption[0]?.value}
							>
								<SelectTrigger
									id="division-select"
									className={clsx('bg-white focus:ring-0 h-10', {
										hidden: divisionsWithAllOption.length === 1
									})}
									icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
								>
									<SelectValue placeholder="Select division" />
								</SelectTrigger>
								<SelectContent>
									{divisionsWithAllOption.map((division) => (
										<SelectItem key={division.value} value={division.value}>
											{division.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="mt-4 flex items-center justify-end">
							<Button
								variant="secondary"
								className="mr-2 flex text-sm"
								onClick={handleReset}
								data-testid="players-button-reset"
								size="lg"
							>
								Reset
							</Button>
							<Button
								className={'!flex w-fit !justify-center !text-sm bg-orange-500 hover:bg-orange-700 text-white'}
								size="lg"
								onClick={() => {
									setFilters({ ...filters, mobile_filters: '' });
									router.replace(`?${getNewSearchParams(filters)?.toString()}`);
								}}
							>
								Apply
							</Button>
						</div>
					</div>
				</DrawerContent>
			</DrawerPortal>
		</Drawer>
	);
};
